// PosPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import useAxiosInstance from "../../../../Utilities/Hooks/useAxiosInstance";
import "./PosPage.css";
import { PRODUCTSIZE_API, ORDERS_API } from "../../../../Utilities/APIs/APIs";
import PosInvoice from "./PosInvoice";

const API_BASE = "https://api.stridesco.com";
const POS_CREATE_URL = `${API_BASE}/api/employee/orders/pos/createOrder`;
const STOCKUNITS_BASE = "/inventory/stockUnits/";
const CDN_BASE = "https://stridesco.sgp1.digitaloceanspaces.com/";
const CUSTOMER_SEARCH_URL = `${API_BASE}/api/employee/customers/getAllCustomers?search=`;

// ✅ your login endpoint (you showed this in network)
const LOGIN_URL = `${API_BASE}/api/public/employeeAuth/login`;

function PosPage() {
  // -------------------------
  //  STATE
  // -------------------------
  const [barcodeInput, setBarcodeInput] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState("");
  const [lastScan, setLastScan] = useState(null);

  const [cartItems, setCartItems] = useState([]);

  const [discountCode, setDiscountCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);

  const [promotion, setPromotion] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoMessage, setPromoMessage] = useState("");

  const [customer, setCustomer] = useState({ name: "", mobile: "", email: "" });

  const [step, setStep] = useState("scan");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [cashReceived, setCashReceived] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🔹 These two hold the last created POS order
  const [placedOrder, setPlacedOrder] = useState(null);
  const [placedOrderItems, setPlacedOrderItems] = useState([]);

  const scanInputRef = useRef(null);
  const axiosInstance = useAxiosInstance();

  const [customerLookupMessage, setCustomerLookupMessage] = useState("");
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [lastFetchedPhone, setLastFetchedPhone] = useState(""); // IMPORTANT (kept)

  // ✅ Additional instructions (INSIDE payment step)
  const [additionalInstructions, setAdditionalInstructions] = useState("");

  // ✅ Transaction number for bKash OR Card (card treated as SSL)
  const [transactionNumber, setTransactionNumber] = useState("");

  // ✅ Logged-in staff name (auto from login profile saved locally)
  const [staffName, setStaffName] = useState("");

  const [customerLoyalty, setCustomerLoyalty] = useState({
    points: 0,
    club: "",
  });

  // Auto focus on scan box
  useEffect(() => {
    if (scanInputRef.current) scanInputRef.current.focus();
  }, []);

  // ✅ Load staff name from saved login response
  // Your login API returns { _id, name, email, level, dp, token } (as per screenshot)
  // Usually that object is saved in localStorage after login.
  useEffect(() => {
    const readStaffFromStorage = () => {
      try {
        const keysToTry = [
          "employeeInfo",
          "employee",
          "employeeAuth",
          "userInfo",
          "auth",
          "token", // sometimes stores string only (won't help)
        ];

        for (const k of keysToTry) {
          const raw = localStorage.getItem(k);
          if (!raw) continue;

          // If it's JSON
          if (raw.trim().startsWith("{")) {
            const obj = JSON.parse(raw);

            // common shapes
            const candidate =
              obj?.name ||
              obj?.employee?.name ||
              obj?.user?.name ||
              obj?.data?.name ||
              "";

            if (candidate) return candidate.toString();
          }
        }
      } catch (e) {
        // ignore
      }
      return "";
    };

    const nameFromLS = readStaffFromStorage();
    if (nameFromLS) {
      setStaffName(nameFromLS);
      return;
    }

    // Fallback: if you have token in axiosInstance and an endpoint later, you can add it here
    // But we won't guess more endpoints to avoid breaking.
  }, []);

  // -------------------------
  //  DERIVED VALUES
  // -------------------------
  const subtotal = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) => sum + item.unitPrice * item.barcodes.length,
        0
      ),
    [cartItems]
  );

  // Promo discount derived from promo object
  const promoDiscount = useMemo(() => {
    if (!promotion || !subtotal) return 0;
    if (!promotion.isActive) return 0;

    const amount = Number(promotion.discountAmount) || 0;

    if (promotion.discountType === "percentage") {
      const d = Math.round((subtotal * amount) / 100);
      return d > subtotal ? subtotal : d;
    }

    if (promotion.discountType === "flat") {
      return amount > subtotal ? subtotal : amount;
    }

    return 0;
  }, [promotion, subtotal]);

  // Manual discount only if no promo applied
  const manualDiscount = useMemo(() => {
    if (promotion) return 0;
    const d = Number(discountAmount) || 0;
    if (d < 0) return 0;
    if (d > subtotal) return subtotal;
    return d;
  }, [discountAmount, subtotal, promotion]);

  const safeDiscount = useMemo(
    () => promoDiscount || manualDiscount,
    [promoDiscount, manualDiscount]
  );

  const total = useMemo(() => subtotal - safeDiscount, [subtotal, safeDiscount]);

  const changeDue = useMemo(() => {
    if (paymentMethod !== "cash") return 0;
    const c = Number(cashReceived) || 0;
    return c > total ? c - total : 0;
  }, [paymentMethod, cashReceived, total]);

  // -------------------------
  //  SCAN HANDLER
  // -------------------------
  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    const code = barcodeInput.trim();
    if (!code) return;

    setIsScanning(true);
    setScanError("");
    setLastScan(null);

    try {
      // 1) Lookup stock unit
      const suRes = await axiosInstance.get(
        STOCKUNITS_BASE + "by-code/" + encodeURIComponent(code)
      );
      const stockUnit = suRes.data;

      if (!stockUnit || !stockUnit.productSize) {
        throw new Error("Barcode not recognised.");
      }

      if (stockUnit.status && stockUnit.status !== "available") {
        throw new Error("This barcode is already sold.");
      }

      // 2) Load ProductSize → product + size
      const psRes = await axiosInstance.get(
        PRODUCTSIZE_API + "getSingleProductSize/" + stockUnit.productSize
      );
      const ps = psRes.data;

      if (!ps.product || !ps.size) throw new Error("Product/Size info missing.");

      const product = ps.product;
      const size = ps.size;

      // 3) Save last scanned item
      setLastScan({
        stockUnitId: stockUnit._id || null,
        productSizeId: ps._id,
        productId: product._id,
        sizeId: size._id,
        barcode: stockUnit.code || code,

        squId: product.squId,
        productName: product.name,
        sizeName: size.name,

        productLocation: stockUnit.productLocation || null,

        oldPrice: product.price,
        unitPrice: product.currentPrice || product.price,

        cardFrontImage: product.cardFrontImage,
      });

      setBarcodeInput("");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to scan barcode";
      setScanError(msg);
    } finally {
      setIsScanning(false);
      if (scanInputRef.current) scanInputRef.current.focus();
    }
  };

  // -------------------------
  //  ADD LAST SCAN TO CART
  // -------------------------
  const handleAddLastScanToCart = () => {
    if (!lastScan) return;

    setCartItems((prev) => {
      const idx = prev.findIndex(
        (it) =>
          it.productId === lastScan.productId &&
          it.sizeId === lastScan.sizeId &&
          it.unitPrice === lastScan.unitPrice
      );

      const barcodeObj = {
        code: lastScan.barcode,
        stockUnitId: lastScan.stockUnitId,
      };

      if (idx === -1) {
        return [
          ...prev,
          {
            id: `${lastScan.productId}-${lastScan.sizeId}-${Date.now()}`,
            productId: lastScan.productId,
            sizeId: lastScan.sizeId,
            squId: lastScan.squId,
            productName: lastScan.productName,
            sizeName: lastScan.sizeName,
            unitPrice: lastScan.unitPrice,
            oldPrice: lastScan.oldPrice,
            productLocation: lastScan.productLocation,
            barcodes: [barcodeObj],
          },
        ];
      }

      const updated = [...prev];
      if (!updated[idx].barcodes.some((b) => b.code === barcodeObj.code)) {
        updated[idx].barcodes.push(barcodeObj);
      }
      return updated;
    });

    setLastScan(null);
    setScanError("");
    if (scanInputRef.current) scanInputRef.current.focus();
  };

  // -------------------------
  //  CART MODIFY FUNCTIONS
  // -------------------------
  const handleRemoveCartItem = (id) => {
    setCartItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleDecreaseQty = (id) => {
    setCartItems((prev) =>
      prev
        .map((it) => {
          if (it.id !== id) return it;
          if (it.barcodes.length <= 1) return null;
          const b = [...it.barcodes];
          b.pop();
          return { ...it, barcodes: b };
        })
        .filter(Boolean)
    );
  };

  const handleIncreaseQty = () => {
    alert("To increase quantity, please scan another barcode.");
    if (scanInputRef.current) scanInputRef.current.focus();
  };

  // -------------------------
  //  CHECKOUT STEPS
  // -------------------------
  const canCheckout = cartItems.length > 0;

  const handleGoToCustomer = () => {
    if (!canCheckout) return;
    setStep("customer");
  };

  const handleApplyPromoCode = async () => {
    const typed = (discountCode || "").trim();
    if (!typed) {
      setPromotion(null);
      setPromoMessage("");
      setPromoError("Please enter a promo code.");
      return;
    }

    try {
      setPromoError("");
      setPromoMessage("Checking...");

      const res = await axiosInstance.get(
        "/promotions/getPromotionByPromoCode/" + encodeURIComponent(typed)
      );
      const promo = res.data;

      // For POS we only allow unlimited promos
      if (promo.promoType !== "unlimited") {
        setPromotion(null);
        setPromoMessage("");
        setPromoError("This promo code is not allowed in POS.");
        return;
      }

      setPromotion(promo);
      setDiscountCode(promo.promoCode || typed);
      setPromoMessage(`Code ${promo.promoCode || typed} applied.`);
      setPromoError("");

      // Clear manual discount to avoid confusion
      setDiscountAmount(0);
    } catch (error) {
      const msg = error?.response?.data?.message ?? "";
      setPromotion(null);
      setPromoMessage("");
      setPromoError(msg || "This promo code is not valid at the moment.");
    }
  };

  // (kept as-is even though not used currently)
  const normalizePhone = (v = "") => String(v).trim();

  // (kept as-is even though not used currently)
  const handlePhoneChange = (val) => {
    const nextPhone = normalizePhone(val);

    setCustomer((prev) => ({ ...prev, phone: nextPhone }));

    if (lastFetchedPhone && nextPhone !== lastFetchedPhone) {
      setIsExistingCustomer(false);
      setCustomer((prev) => ({
        ...prev,
        name: "",
        email: "",
        phone: nextPhone,
      }));
      setCustomerLoyalty({ points: 0, club: "" });
      setCustomerLookupMessage("");
    }
  };

  const handleLookupCustomer = async () => {
    const email = (customer.email || "").trim().toLowerCase();

    if (!email) {
      setCustomerLookupMessage("Please enter email first.");
      setIsExistingCustomer(false);

      // clear details
      setCustomer((prev) => ({ ...prev, name: "", mobile: "" }));
      setCustomerLoyalty(null);
      return;
    }

    try {
      setCustomerLookupMessage("Searching customer...");
      setIsExistingCustomer(false);

      // reset old customer info while searching
      setCustomer((prev) => ({ ...prev, name: "", mobile: "" }));
      setCustomerLoyalty(null);

      const { data } = await axiosInstance.get(
        `${CUSTOMER_SEARCH_URL}${encodeURIComponent(email)}&page=1`
      );

      const list = Array.isArray(data?.customers) ? data.customers : [];

      const found = list.find(
        (c) => (c?.email || "").toLowerCase().trim() === email
      );

      if (!found) {
        setIsExistingCustomer(false);
        setCustomerLookupMessage("New customer – please enter name and mobile.");

        setCustomer((prev) => ({
          ...prev,
          email,
          name: "",
          mobile: "",
        }));
        setCustomerLoyalty(null);
        return;
      }

      setCustomer({
        name: found.name || "",
        email: found.email || email,
        mobile: found.mobile || "",
      });

      setCustomerLoyalty({
        club: found.club || "Bronze",
        points: Number(found.totalPoints || 0),
      });

      setIsExistingCustomer(true);
      setCustomerLookupMessage("Existing customer loaded.");
    } catch (err) {
      console.error("lookup customer error", err);

      setIsExistingCustomer(false);
      setCustomerLookupMessage("New customer – please enter name and mobile.");

      setCustomer((prev) => ({
        ...prev,
        email,
        name: "",
        mobile: "",
      }));
      setCustomerLoyalty(null);
    }
  };

  const handleGoToPayment = async () => {
    const email = (customer.email || "").trim();
    const name = (customer.name || "").trim();

    if (!email) {
      alert("Please enter customer email.");
      return;
    }

    if (!name) {
      await handleLookupCustomer();
    }

    const finalName = (customer.name || "").trim();

    if (!finalName) {
      alert("Please enter customer name for a new customer.");
      return;
    }

    setStep("payment");
  };

  const handleGoToConfirm = () => {
    if (!paymentMethod) {
      alert("Select a payment method.");
      return;
    }

    if (paymentMethod === "cash") {
      const cash = Number(cashReceived) || 0;
      if (cash < total) {
        alert("Cash received is less than total.");
        return;
      }
    }

    // ✅ txn number required for card (ssl) + bkash
    if ((paymentMethod === "card" || paymentMethod === "bkash") && !transactionNumber.trim()) {
      alert("Please enter transaction number.");
      return;
    }

    setStep("confirm");
  };

  // -------------------------
  //  PLACE ORDER
  // -------------------------
  const handlePlaceOrder = async () => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const orderItems = cartItems.map((it) => ({
        item: it.productId,
        size: it.sizeId,
        quantity: it.barcodes.length,
        unitPrice: it.unitPrice,
        totalPrice: it.unitPrice * it.barcodes.length,
        original: `${it.squId}-${it.sizeName}`,
        barcodes: it.barcodes.map((b) => b.code),
      }));

      const payload = {
        orderCustomerName: customer.name,
        orderCustomerEmail: customer.email,
        orderCustomerPhone: customer.mobile,

        // ✅ additional instructions
        additionalInstructions,

        // ✅ card treated as SSL
        sslTransactionNumber: paymentMethod === "card" ? transactionNumber.trim() : "",
        bkashTransactionNumber: paymentMethod === "bkash" ? transactionNumber.trim() : "",

        orderSubtotal: subtotal,
        promoDiscountAmount: safeDiscount,
        orderTotal: total,
        paymentMethod,
        promoCode: promotion ? promotion.promoCode : null,
        posMeta: {
          counterId: "POS-1",
          staffId: staffName || "", // ✅ staff from profile
          discountCode: promotion ? promotion.promoCode : discountCode || null,
        },
        orderItems,
      };

      const { data } = await axiosInstance.post(POS_CREATE_URL, payload);

      const orderFromServer = data.order || data;

      let itemsFromServer = [];
      if (orderFromServer?._id) {
        try {
          const itemsRes = await axiosInstance.get(
            ORDERS_API + "orderItemsFromOrder/" + orderFromServer._id
          );
          itemsFromServer = itemsRes.data.orderItems || [];
        } catch (err) {
          console.error("Failed to fetch populated items for invoice", err);
        }
      }

      setPlacedOrder(orderFromServer);
      setPlacedOrderItems(itemsFromServer);

      setStep("success");

      // reset for next order (success view still has placedOrder*)
      setCartItems([]);
      setDiscountCode("");
      setDiscountAmount(0);
      setPromotion(null);
      setPromoError("");
      setPromoMessage("");
      setPaymentMethod("");
      setCashReceived("");
      setLastScan(null);
      setBarcodeInput("");

      // ✅ reset these too
      setAdditionalInstructions("");
      setTransactionNumber("");

      if (scanInputRef.current) scanInputRef.current.focus();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to place order";
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewOrder = () => {
    window.location.reload();
  };

  const handlePrintInvoice = () => {
    if (!placedOrder) return;
    PosInvoice(placedOrder, placedOrderItems);
  };

  // --------------------------------------------------------------------
  //  UI RENDER
  // --------------------------------------------------------------------
  return (
    <div className="pos-page">
      {/* HEADER */}
      <div className="pos-header">
        <div>
          <div className="pos-title">STRIDES CO POS</div>
          <div className="pos-subtitle">In-Store Checkout</div>
        </div>

        <div className="pos-header-right">
          <div className="pos-header-pill">
            Counter <strong>POS-1</strong>
          </div>
          <div className="pos-header-pill">
            Staff <strong>{staffName || "ADMIN"}</strong>
          </div>
        </div>
      </div>

      <div className="pos-main">
        {/* LEFT SIDE */}
        <div className="pos-left">
          {/* Scan Panel */}
          <div className="pos-panel">
            <div className="pos-panel-header">Scan Product</div>

            <form onSubmit={handleBarcodeSubmit} className="pos-scan-form">
              <input
                ref={scanInputRef}
                className="pos-input pos-input-barcode"
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder="Scan barcode here..."
                autoComplete="off"
              />

              <button
                type="submit"
                className="pos-btn pos-btn-primary"
                disabled={isScanning || !barcodeInput.trim()}
              >
                {isScanning ? "Scanning..." : "Scan"}
              </button>
            </form>

            {scanError && <div className="pos-error">{scanError}</div>}
            {!scanError && (
              <div className="pos-hint">
                Tip: Keep the cursor in the scan box for fastest flow.
              </div>
            )}
          </div>

          {/* Last Scan Panel */}
          <div className="pos-panel">
            <div className="pos-panel-header">Last Scan</div>

            {lastScan ? (
              <div className="pos-lastscan-card">
                <div className="pos-lastscan-image-wrapper">
                  <img
                    src={
                      lastScan.cardFrontImage
                        ? `${CDN_BASE}${lastScan.cardFrontImage}`
                        : "/images/placeholder-product.png"
                    }
                    alt={lastScan.productName}
                    className="pos-lastscan-image"
                  />
                </div>

                <div className="pos-lastscan-info">
                  <div className="pos-lastscan-name">{lastScan.productName}</div>

                  <div className="pos-lastscan-detail">
                    Size: <strong>{lastScan.sizeName}</strong>
                  </div>
                  <div className="pos-lastscan-detail">
                    Barcode: <strong>{lastScan.barcode}</strong>
                  </div>

                  <div className="pos-last-price-row">
                    {lastScan.oldPrice !== lastScan.unitPrice && (
                      <span className="pos-price-old">{lastScan.oldPrice} ৳</span>
                    )}
                    <span className="pos-price-current">
                      Price: {lastScan.unitPrice} ৳
                    </span>
                  </div>

                  <div className="pos-lastscan-actions">
                    <button
                      type="button"
                      className="pos-btn pos-btn-primary"
                      onClick={handleAddLastScanToCart}
                    >
                      Add to Cart
                    </button>
                    <button
                      type="button"
                      className="pos-btn pos-btn-ghost"
                      onClick={() => setLastScan(null)}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="pos-empty">Scan a product to preview here.</div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE – CART + STEPS */}
        <div className="pos-right">
          <div className="pos-panel pos-cart-panel">
            <div className="pos-panel-header pos-cart-header">
              <span>Cart</span>
              <span className="pos-cart-count">
                {cartItems.reduce((s, it) => s + it.barcodes.length, 0)} item(s)
              </span>
            </div>

            {/* Cart List */}
            {cartItems.length === 0 ? (
              <div className="pos-empty">No items in cart.</div>
            ) : (
              <div className="pos-cart-list">
                {cartItems.map((it) => {
                  const qty = it.barcodes.length;
                  const lineTotal = it.unitPrice * qty;

                  return (
                    <div className="pos-cart-line" key={it.id}>
                      <div className="pos-cart-line-main">
                        <div className="pos-cart-line-name">{it.productName}</div>

                        <div className="pos-cart-line-meta">
                          Size: {it.sizeName}
                          <br />
                          Barcodes: {it.barcodes.map((b) => b.code).join(", ")}
                        </div>

                        <div className="pos-cart-line-price-row">
                          {it.oldPrice !== it.unitPrice && (
                            <span className="pos-price-old">{it.oldPrice} ৳</span>
                          )}
                          <span className="pos-price-current">{it.unitPrice} ৳</span>
                        </div>
                      </div>

                      <div className="pos-cart-line-right">
                        <div className="pos-cart-qty">
                          <button
                            type="button"
                            onClick={() => handleDecreaseQty(it.id)}
                            aria-label="Decrease quantity"
                          >
                            –
                          </button>
                          <span>{qty}</span>
                          <button
                            type="button"
                            onClick={handleIncreaseQty}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>

                        <div className="pos-cart-line-total">{lineTotal} ৳</div>

                        <button
                          type="button"
                          className="pos-cart-remove"
                          onClick={() => handleRemoveCartItem(it.id)}
                          aria-label="Remove item"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Discount Section */}
            <div className="pos-panel-divider" />

            <div className="pos-discount-row">
              {/* Promo code side */}
              <div className="pos-discount-left">
                <label className="pos-label">Discount Code</label>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleApplyPromoCode();
                  }}
                  className="pos-discount-code-row"
                >
                  <input
                    className="pos-input pos-input-sm pos-discount-input"
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value);
                      setPromoMessage("");
                      setPromoError("");
                      setPromotion(null);
                    }}
                    placeholder="Enter code"
                  />

                  <button
                    type="submit"
                    className="pos-btn-apply"
                    disabled={!discountCode.trim()}
                    title={!discountCode.trim() ? "Enter a code first" : "Apply promo"}
                  >
                    Apply
                  </button>
                </form>

                {promoMessage && !promoError && (
                  <div className="pos-promo-msg">{promoMessage}</div>
                )}
                {promoError && <div className="pos-promo-error">{promoError}</div>}
              </div>

              {/* Extra manual discount side */}
              <div className="pos-discount-right">
                <label className="pos-label">Extra Discount (৳)</label>
                <input
                  className="pos-input pos-input-sm"
                  type="number"
                  min="0"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  disabled={!!promotion}
                />
                {promotion && (
                  <div className="pos-promo-note">
                    Promo code applied – extra discount disabled.
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="pos-summary">
              <div className="pos-summary-row">
                <span>Subtotal</span>
                <span>{subtotal} ৳</span>
              </div>
              <div className="pos-summary-row">
                <span>Discount</span>
                <span>- {safeDiscount} ৳</span>
              </div>
              <div className="pos-summary-row pos-summary-total">
                <span>Total</span>
                <span>{total} ৳</span>
              </div>
            </div>

            {/* ------------------ Steps ------------------ */}
            {step === "scan" && (
              <div className="pos-actions">
                <button
                  className="pos-btn pos-btn-primary pos-btn-full"
                  disabled={!canCheckout}
                  onClick={handleGoToCustomer}
                >
                  Checkout
                </button>
              </div>
            )}

            {step === "customer" && (
              <div className="pos-step-card">
                <div className="pos-step-title">Customer Details</div>

                {/* ROW 1: Email + Search + Loyalty */}
                <div className="pos-customer-row">
                  <div className="pos-field pos-field-grow">
                    <label className="pos-label">Email *</label>

                    <div className="pos-phone-row">
                      <input
                        className="pos-input pos-input-sm"
                        type="text"
                        placeholder="abc@gmail.com"
                        value={customer.email}
                        onChange={(e) =>
                          setCustomer((prev) => ({ ...prev, email: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleLookupCustomer();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="pos-btn pos-btn-fetch"
                        onClick={handleLookupCustomer}
                      >
                        Search
                      </button>

                      {isExistingCustomer && customerLoyalty && (
                        <div className="pos-loyalty-inline">
                          <span className="pos-loyalty-chip pos-loyalty-club">
                            {customerLoyalty.club}
                          </span>

                          <span className="pos-loyalty-chip pos-loyalty-points">
                            {Math.round(customerLoyalty.points).toLocaleString()} pts
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ROW 2: Name + Mobile */}
                <div className="pos-customer-row pos-customer-row-2col">
                  <div className="pos-field">
                    <label className="pos-label">
                      Name {isExistingCustomer ? "" : "*"}
                    </label>
                    <input
                      className="pos-input pos-input-sm"
                      type="text"
                      value={customer.name}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Customer name"
                    />
                  </div>

                  <div className="pos-field">
                    <label className="pos-label">Mobile</label>
                    <input
                      className="pos-input pos-input-sm"
                      type="number"
                      value={customer.mobile}
                      onChange={(e) =>
                        setCustomer((prev) => ({ ...prev, mobile: e.target.value }))
                      }
                      placeholder="Optional"
                    />
                  </div>
                </div>

                {customerLookupMessage && (
                  <div
                    className={
                      isExistingCustomer ? "pos-note pos-note-success" : "pos-note"
                    }
                  >
                    {customerLookupMessage}
                  </div>
                )}

                <div className="pos-step-actions">
                  <button
                    type="button"
                    className="pos-btn pos-btn-ghost"
                    onClick={() => setStep("scan")}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="pos-btn pos-btn-primary"
                    onClick={handleGoToPayment}
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {step === "payment" && (
              <div className="pos-step-card">
                <div className="pos-step-title">Payment Method</div>

                <div className="pos-payment-segment">
                  {["cash", "card", "bkash"].map((method) => {
                    const label =
                      method === "cash"
                        ? "Cash"
                        : method === "card"
                        ? "Card"
                        : "Mobile Banking";

                    return (
                      <button
                        key={method}
                        type="button"
                        className={
                          "pos-payment-seg-btn" +
                          (paymentMethod === method
                            ? " pos-payment-seg-btn-active"
                            : "")
                        }
                        onClick={() => setPaymentMethod(method)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {paymentMethod === "cash" && (
                  <div className="pos-cash-grid">
                    <div className="pos-cash-box">
                      <label className="pos-label">Cash Received (৳)</label>
                      <input
                        className="pos-input pos-input-sm pos-cash-input-full"
                        type="number"
                        min="0"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                      />
                      <div className="pos-hint">
                        Must be greater than or equal to total amount.
                      </div>
                    </div>

                    <div className="pos-cash-box">
                      <label className="pos-label">Change</label>
                      <div className="pos-cash-change-value">
                        {changeDue}{" "}
                        <span className="pos-cash-change-currency">৳</span>
                      </div>
                    </div>
                  </div>
                )}

                {(paymentMethod === "card" || paymentMethod === "bkash") && (
                  <div className="pos-cash-box" style={{ marginTop: 10 }}>
                    <label className="pos-label">
                      {paymentMethod === "card"
                        ? "SSL Transaction Number"
                        : "bKash Transaction Number"}
                    </label>
                    <input
                      className="pos-input pos-input-sm"
                      type="text"
                      value={transactionNumber}
                      onChange={(e) => setTransactionNumber(e.target.value)}
                      placeholder="Enter transaction number"
                    />
                  </div>
                )}

                {/* ✅ FIX: additional instructions directly inside payment step */}
                <div className="pos-customer-row" style={{ marginTop: 12 }}>
                  <div className="pos-field pos-field-grow">
                    <label className="pos-label">Additional Instructions</label>
                    <input
                      className="pos-input pos-input-sm"
                      type="text"
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      placeholder="Any note for this order (optional)"
                    />
                  </div>
                </div>

                <div className="pos-step-actions">
                  <button
                    className="pos-btn pos-btn-ghost"
                    onClick={() => setStep("customer")}
                  >
                    Back
                  </button>
                  <button
                    className="pos-btn pos-btn-primary"
                    onClick={handleGoToConfirm}
                  >
                    Review &amp; Place Order
                  </button>
                </div>
              </div>
            )}

            {step === "confirm" && (
              <div className="pos-step-card">
                <div className="pos-step-title">Confirm Order</div>

                <div className="pos-confirm-block">
                  <div>
                    <strong>Customer Name:</strong> {(customer.name || "").trim() || "—"}
                  </div>

                  <div>
                    <strong>Mobile: </strong>
                    {(customer.mobile || "").toString().trim()}
                  </div>

                  {customer.email && (
                    <div>
                      <strong>Email:</strong> {customer.email}
                    </div>
                  )}

                  <div>
                    <strong>Payment:</strong>{" "}
                    {paymentMethod === "cash"
                      ? "Cash"
                      : paymentMethod === "card"
                      ? "Card"
                      : "Mobile Banking"}
                  </div>

                  {(paymentMethod === "card" || paymentMethod === "bkash") &&
                    transactionNumber.trim() && (
                      <div>
                        <strong>
                          {paymentMethod === "card" ? "SSL Txn:" : "bKash Txn:"}
                        </strong>{" "}
                        {transactionNumber.trim()}
                      </div>
                    )}

                  {additionalInstructions.trim() && (
                    <div>
                      <strong>Instructions:</strong> {additionalInstructions.trim()}
                    </div>
                  )}

                  <div>
                    <strong>Items:</strong>{" "}
                    {cartItems.reduce((sum, it) => sum + it.barcodes.length, 0)}
                  </div>

                  <div>
                    <strong>Total:</strong> {total} ৳
                  </div>

                  {/* ✅ show staff who placed it */}
                  <div>
                    <strong>Placed By:</strong> {staffName || "ADMIN"}
                  </div>
                </div>

                {submitError && (
                  <div className="pos-error pos-error-top">{submitError}</div>
                )}

                <div className="pos-step-actions">
                  <button
                    className="pos-btn pos-btn-ghost"
                    onClick={() => setStep("payment")}
                  >
                    Back
                  </button>

                  <button
                    className="pos-btn pos-btn-primary"
                    disabled={isSubmitting}
                    onClick={handlePlaceOrder}
                  >
                    {isSubmitting ? "Placing..." : "Place Order"}
                  </button>
                </div>
              </div>
            )}

            {step === "success" && (
              <div className="pos-step-card pos-step-success">
                <div className="pos-step-title">ORDER PLACED SUCCESSFULLY</div>
                <div className="pos-success-icon">✓</div>

                <div className="pos-confirm-block">
                  <div>
                    <strong>Invoice:</strong>{" "}
                    {placedOrder?.invoiceNo || "(not available)"}
                  </div>

                  <div>
                    <strong>Total:</strong>{" "}
                    {placedOrder?.orderTotal != null
                      ? `${placedOrder.orderTotal} ৳`
                      : "৳"}
                  </div>

                  {/* ✅ show staff who placed it */}
                  <div>
                    <strong>Placed By:</strong> {staffName || "ADMIN"}
                  </div>
                </div>

                <div className="pos-step-actions pos-step-actions-center">
                  <button
                    type="button"
                    className="pos-btn pos-btn-secondary"
                    onClick={handlePrintInvoice}
                  >
                    Print Invoice
                  </button>

                  <button
                    className="pos-btn pos-btn-primary"
                    onClick={handleNewOrder}
                  >
                    New Order
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PosPage;
